//-- copyright
// OpenProject Resource Planner (multi-resource) management plugin.
//++

import { TpCalendar } from '../shared/timephased.util';

export interface RpTag { id:number; name:string; color:string; description:string|null; }
// The calendar definition is the v2 MS-Project-parity shape resolved by the
// backend (see CalendarDefinition). TpCalendar carries weekDays / workWeeks /
// exceptions / parentCalendarId / conversion factors.
export type RpCalDefinition = TpCalendar;
export interface RpCalendar {
  id:number; name:string; description:string|null; isDefault:boolean; definition:RpCalDefinition;
}
export interface RpRateRow { effectiveDate:string|null; rate:number; }
export type RpRateTables = Record<string, RpRateRow[]>;
export interface RpAvailability { from:string|null; to:string|null; maxUnits:number; }
export interface RpResource {
  id:number; isGroup:boolean; name:string; initials:string|null; resourceType:string|null;
  maxUnits:number; rate:number; currency:string;
  unitLabel:string|null; calendarId:number|null; userId:number|null; userName:string|null;
  tagIds:number[];
  rateTables:RpRateTables; availabilities:RpAvailability[];
  children?:RpResource[];
}
export interface RpData {
  canManage:boolean; canAssign?:boolean; tags:RpTag[]; calendars:RpCalendar[]; resources:RpResource[];
}

// Minimal HTTP helper using fetch + the page CSRF token (same pattern as the WP tab).
export class ResourcePlannerService {
  constructor(private projectId:string) {}

  private base = `/projects/${this.projectId}/resource_planner/api`;

  private csrf():string {
    const el = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement|null;
    return el ? el.content : '';
  }

  private req(method:string, url:string, body?:unknown):Promise<any> {
    const headers:Record<string,string> = { Accept: 'application/json' };
    if (body !== undefined) { headers['Content-Type'] = 'application/json'; }
    if (method !== 'GET') { headers['X-CSRF-Token'] = this.csrf(); }
    return fetch(url, {
      method, headers, credentials: 'same-origin',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }).then((r) => {
      if (r.status === 204) { return null; }
      return r.json().then((j) => (r.ok ? j : Promise.reject(j)));
    });
  }

  data():Promise<RpData> { return this.req('GET', `${this.base}/data`); }

  createResource(payload:Record<string,unknown>):Promise<RpResource> {
    return this.req('POST', `${this.base}/resources`, { resource: payload });
  }
  updateResource(id:number, payload:Record<string,unknown>):Promise<RpResource> {
    return this.req('PATCH', `${this.base}/resources/${id}`, { resource: payload });
  }
  deleteResource(id:number):Promise<null> { return this.req('DELETE', `${this.base}/resources/${id}`); }

  createTag(payload:Record<string,unknown>):Promise<RpTag> {
    return this.req('POST', `${this.base}/tags`, { tag: payload });
  }
  updateTag(id:number, payload:Record<string,unknown>):Promise<RpTag> {
    return this.req('PATCH', `${this.base}/tags/${id}`, { tag: payload });
  }
  deleteTag(id:number):Promise<null> { return this.req('DELETE', `${this.base}/tags/${id}`); }

  createCalendar(payload:Record<string,unknown>):Promise<RpCalendar> {
    return this.req('POST', `${this.base}/calendars`, { calendar: payload });
  }
  updateCalendar(id:number, payload:Record<string,unknown>):Promise<RpCalendar> {
    return this.req('PATCH', `${this.base}/calendars/${id}`, { calendar: payload });
  }
  deleteCalendar(id:number):Promise<null> { return this.req('DELETE', `${this.base}/calendars/${id}`); }
}
